import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { getMe, getMySessions, UserSessionBrief } from '../services/adminApi'

interface UserContextType {
  userApiKey: string
  setUserApiKey: (key: string) => void
  keyLevel: 'standard' | 'admin' | null
  isLoggedIn: boolean
  isAdmin: boolean
  requireApiKeys: boolean
  sessions: UserSessionBrief[]
  loginError: string
  login: (key: string) => Promise<boolean>
  logout: () => void
  refreshSessions: () => Promise<void>
}

const UserContext = createContext<UserContextType>(null!)

export function UserProvider({ children }: { children: ReactNode }) {
  const [userApiKey, setStoredKey] = useLocalStorage('user_api_key', '')
  const [keyLevel, setKeyLevel] = useState<'standard' | 'admin' | null>(null)
  const [requireApiKeys, setRequireApiKeys] = useState(true)
  const [sessions, setSessions] = useState<UserSessionBrief[]>([])
  const [loginError, setLoginError] = useState('')

  const isLoggedIn = keyLevel !== null
  const isAdmin = keyLevel === 'admin'

  async function login(key: string): Promise<boolean> {
    setLoginError('')
    try {
      const record = await getMe(key)
      setStoredKey(key)
      setKeyLevel(record.level)
      const sessionList = await getMySessions(key)
      setSessions(sessionList)
      return true
    } catch {
      setLoginError('Invalid or revoked API key')
      return false
    }
  }

  function logout() {
    setStoredKey('')
    setKeyLevel(null)
    setSessions([])
  }

  function setUserApiKey(key: string) {
    setStoredKey(key)
  }

  async function refreshSessions() {
    if (!userApiKey) return
    try {
      const result = await getMySessions(userApiKey)
      setSessions(result)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetch('/api/mode')
      .then(r => r.json())
      .then((data: { requireApiKeys: boolean }) => setRequireApiKeys(data.requireApiKeys))
      .catch(() => {})

    if (!userApiKey) return
    getMe(userApiKey)
      .then(async record => {
        setKeyLevel(record.level)
        const sessionList = await getMySessions(userApiKey)
        setSessions(sessionList)
      })
      .catch(() => {
        setStoredKey('')
        setKeyLevel(null)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UserContext.Provider value={{ userApiKey, setUserApiKey, keyLevel, isLoggedIn, isAdmin, requireApiKeys, sessions, loginError, login, logout, refreshSessions }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => useContext(UserContext)
